import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { shallowEqual, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Grid } from 'elements';
import { RootState } from 'modules';
import Reply from 'components/base/reply/';
import { TDetailType } from 'components/case/parts/Detail';
import Information from 'components/case/parts/Information';
import MenteeExplain from 'components/case/parts/MenteeExplain';
import MentorExplain from 'components/case/parts/MentorExplain';
import Purchase from 'components/case/parts/Purchase';
import Questions from 'components/case/parts/Questions';
import StepExplain from 'components/case/parts/StepExplain';
import { showAlert, showConfirm } from 'components/modal/showConfirm';
import {
  getCaseItem,
  getCasePrice,
  checkPaidExplain,
  getCaseMentorExplain,
  getCaseMenteeExplain,
  getCaseStepExplain,
  getCaseReply,
  createCaseReply,
  updateCaseReply,
  likeCaseReply,
  delCaseReply,
} from 'libs/api/case';
import {
  TCase,
  TCasePrice,
  TCaseExplain,
  TRequestMenteeCaseList,
  TCaseMenteeExplain,
  TRequestMentorCaseList,
} from 'modules/case/types';
import { CWindowWidth } from 'constants/base.const';
import { Grid as AGrid, Button, Spin } from 'antd';
import { Colors } from 'GlobalStyles';

const { useBreakpoint } = AGrid;

const Item = () => {
  const history = useHistory();
  const menteeExplainLimit = 5;
  const { id }: { id: string } = useParams();
  const screens = useBreakpoint();
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);

  const [isExplain, setIsExplain] = useState(false);
  const [stepExplains, setStepExplains] = useState<Array<TCaseExplain>>();
  const [mentorExplains, setMentorExplains] = useState<Array<TCaseExplain>>();
  const [menteeExplains, setMenteeExplains] = useState<Array<TCaseMenteeExplain>>();
  const [menteeExplainPage, setMenteeExplainPage] = useState(1);
  const [menteeExplainOrder, setMenteeExplainOrder] = useState('rating');
  const [menteeExplainTotal, setMenteeExplainTotal] = useState(0);
  const [price, setPrice] = useState<TCasePrice>();
  const [cs, setCs] = useState<TCase>();

  const getCase = async () => {
    const { success, data } = await getCaseItem(id);
    if (success) setCs(data.result);
  };

  const getPrice = async () => {
    if (cs && cs.season) {
      const { success, data } = await getCasePrice(id, { season: cs.season });
      if (success) setPrice(data);
    }
  };

  const getStepExplain = async () => {
    const { success, data } = await getCaseStepExplain(id);
    if (success) setStepExplains(data);
  };
  const getMentorExplain = async (ordering = 'rating') => {
    const params: TRequestMentorCaseList = {};
    params[ordering as keyof TRequestMentorCaseList] = ordering;
    const { success, data } = await getCaseMentorExplain(id, params);
    if (success) setMentorExplains(data);
  };
  const getMenteeExplain = async () => {
    let params: any = {
      current: menteeExplainPage,
      pageSize: menteeExplainLimit,
    };
    if (menteeExplainOrder) params[menteeExplainOrder] = menteeExplainOrder;
    const { success, data } = await getCaseMenteeExplain(id, params as TRequestMenteeCaseList);
    if (success) {
      setMenteeExplainTotal(data.total);
      setMenteeExplains(data.contents);
    }
  };

  const select = async (type: keyof TDetailType, selected: number) => {
    if (type === 'mentee') history.push(`/case/board/${id}/${type}/${selected}`);
    else {
      const { success, error } = await checkPaidExplain(selected);
      if (success) history.push(`/case/board/${id}/${type}/${selected}`);
      else {
        if (error.response.data.error_code === '-400002') {
          if (type === 'mentor') {
            showConfirm(
              () => history.push(`/purchase/case?id=${id}&p_type=mentor`),
              '',
              '구매가 필요한 상품입니다. 구매 페이지로 이동하시겠습니까?',
            );
          } else {
            showAlert(undefined, '', '해당 해설은 구매 후 열람하실 수 있습니다.');
          }
        }
      }
    }
  };

  useEffect(() => {
    if (cs) getPrice();
  }, [cs]);

  useEffect(() => {
    getCase();
    getMentorExplain();
    getStepExplain();
  }, []);

  useEffect(() => {
    getMenteeExplain();
  }, [menteeExplainPage, menteeExplainOrder]);

  useEffect(() => {
    if (screens.lg) setIsExplain(true);
  }, [screens]);

  if (!cs) {
    return (
      <Grid justify='center' align='center' height='100px' margin='0 0 150px 0' md={{ margin: '0 0 80px 0' }}>
        <Spin spinning />
      </Grid>
    );
  }

  return (
    <Grid justify='center' margin='0 0 150px 0' md={{ margin: '0 0 80px 0' }}>
      <Helmet>
        {/* <title>시즌별 케이스 노트 - 2021년 하반기 Case</title> */}
        <title>케이스 노트</title>
      </Helmet>
      <Information item={cs} refresh={getCase} />
      <Grid style={{ borderTop: `1px solid ${Colors.grey1}` }} />
      <Questions item={cs} />
      {isExplain && (
        <>
          <Grid maxWidth={`${CWindowWidth}px`}>
            <Grid style={{ borderTop: `1px solid ${Colors.grey1}` }} />
          </Grid>
          {!!stepExplains && (
            <StepExplain explains={stepExplains} onClick={(selected) => select('step', selected)} />
          )}
          {!!price && <Purchase price={price} id={Number(id)} season={cs.season} isPayed={cs.is_payed} />}
          {!!mentorExplains && (
            <MentorExplain
              explains={mentorExplains}
              getExplains={getMentorExplain}
              onClick={(selected) => select('mentor', selected)}
            />
          )}
          {!!menteeExplains && (
            <MenteeExplain
              id={id}
              explains={menteeExplains}
              limit={menteeExplainLimit}
              total={menteeExplainTotal}
              selectExplain={(id) => select('mentee', id)}
              hasWrite={currentUser?.user_type === 'mentee'}
              page={menteeExplainPage}
              setPage={setMenteeExplainPage}
              order={menteeExplainOrder}
              setOrder={setMenteeExplainOrder}
            />
          )}
        </>
      )}
      {!screens.lg && (
        <Grid maxWidth={`${CWindowWidth}px`} justify='center'>
          <Button className='' style={{ width: '80%', height: 'auto' }} onClick={() => setIsExplain(true)}>
            <Grid isInline padding='15px'>
              {isExplain ? 'Case 문제로 돌아가기' : '이 Case의 모든 해설 보기'}
            </Grid>
          </Button>
        </Grid>
      )}
      {isExplain && (
        <Grid maxWidth={`${CWindowWidth}px`} padding='0 50px' md={{ padding: '0 20px' }}>
          <Grid padding='25px 0' justify='center'>
            <Reply
              page_type='caseNote'
              id={Number(id)}
              getReply={getCaseReply}
              onSave={createCaseReply}
              onEdit={updateCaseReply}
              like={likeCaseReply}
              del={delCaseReply}
            />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default Item;
